import React, { useEffect, useRef, useState } from "react";
import type { User } from "../shared/contracts.ts";
import type {
  VisitAssignment,
  VisitOperationConfig,
} from "../shared/visit-operations.ts";
import type { VisitGroup } from "../shared/visits.ts";
import { api, Banner, Button, Field, Modal } from "./ui.tsx";
import { AddressMapLink } from "./maps.tsx";
import { visitDate } from './visits-overview.tsx';
export type VisitOperationAccess = {
  manager: boolean;
  admin: boolean;
  mapsConfigured?: boolean;
  settings?: VisitOperationConfig;
  assignments: VisitAssignment[];
  sources: Array<{
    id: string;
    name: string;
    tabs: Array<{ id: string; title: string }>;
  }>;
  groups: VisitGroup[];
  users: Array<{ id: string; name: string }>;
};
type Row = {
  id: string;
  street: string;
  number: string;
  neighborhood: string;
  residents: string[];
  status: string;
  version: number;
  latest_visit_at: string | null;
  sheetState: string;
  sheetError?:string|null;
};
type List = {
  items: Row[];
  total: number;
  page: number;
  canCreate: boolean;
  canVisit: boolean;
};
const sheetLabel = (state: string) =>
  ({
    confirmed: "Envio à planilha confirmado",
    pending: "Aguardando envio à planilha",
    pending_configuration: "Planilha aguardando configuração",
    unconfirmed: "Envio à planilha ainda não confirmado",
    imported: "Consultado na origem",
  })[state] ?? "Aguardando conferência";
const textError = (e: unknown) => (e as Error).message;
export function Visits({
  user,
  notify,
}: {
  user: User;
  notify: (s: string) => void;
}) {
  const [access, setAccess] = useState<VisitOperationAccess | null>(null),
    [sourceId, setSource] = useState(""),
    [tabId, setTab] = useState(""),
    [section, setSection] = useState("work"),
    [status, setStatus] = useState("pending"),
    [search, setSearch] = useState(""),
    [page, setPage] = useState(1),
    [list, setList] = useState<List | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [refresh, setRefresh] = useState(0),
    [selected, setSelected] = useState<Row | null>(null),
    [note, setNote] = useState(""),
    [visitPhone, setVisitPhone] = useState(""),
    [creating, setCreating] = useState(false),
    [review, setReview] = useState<any>(null),
    [offset, setOffset] = useState(0);
  const [form, setForm] = useState({
    street: "",
    number: "",
    neighborhood: "",
    resident: "",
    phone: "",
    status: "pending",
    groupId: "",
    subgroupId: "",
  });
  const [assignment, setAssignment] = useState({
      userId: "",
      groupId: "",
      subgroupId: "",
      canCreate: false,
      canVisit: false,
      personal: false,
    }),
    [settings, setSettings] = useState<VisitOperationConfig | null>(null);
  const operation = useRef(crypto.randomUUID()),
    returnFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const c = new AbortController();
    api<VisitOperationAccess>("/visits/access", "GET", undefined, c.signal)
      .then((a) => {
        setAccess(a);
        setSettings(a.settings ?? null);
        setSource((s) =>
          a.sources.some((v) => v.id === s) ? s : (a.sources[0]?.id ?? ""),
        );
      })
      .catch((e) => {
        if (!c.signal.aborted) setError(e.message);
      });
    return () => c.abort();
  }, [user.id, refresh]);
  const tabs = access?.sources.find((s) => s.id === sourceId)?.tabs ?? [];
  useEffect(() => {
    setTab((t) => (tabs.some((v) => v.id === t) ? t : (tabs[0]?.id ?? "")));
  }, [sourceId, access]);
  useEffect(() => {
    setList(null);
    setReview(null);
    setPage(1);
    setOffset(0);
  },[sourceId,tabId]);
  useEffect(()=>{
    const a = access?.assignments.find(
      (a) => a.sourceId === sourceId && a.tabId === tabId,
    );
    setAssignment({
      userId: a?.userId ?? "",
      groupId: a?.groupId ?? "",
      subgroupId: a?.subgroupId ?? "",
      canCreate: a?.canCreate ?? false,
      canVisit: a?.canVisit ?? false,
      personal: a?.personal ?? false,
    });
  }, [sourceId, tabId, access]);
  useEffect(() => {
    if (!sourceId || !tabId) return;
    if(section==='delegate')setReview(null);else setList(null);
    const c = new AbortController(),
      timer = setTimeout(() => {
        const p = new URLSearchParams({
          sourceId,
          tabId,
          status,
          search,
          page: String(page),
        });
        if (section === "delegate") {
          p.set("offset", String(offset));
          api("/visits/review?" + p, "GET", undefined, c.signal)
            .then(setReview)
            .catch((e) => {
              if (!c.signal.aborted) setError(e.message);
            });
        } else
          api<List>("/visits/records?" + p, "GET", undefined, c.signal)
            .then(setList)
            .catch((e) => {
              if (!c.signal.aborted) setError(e.message);
            });
      }, 200);
    return () => {
      clearTimeout(timer);
      c.abort();
    };
  }, [sourceId, tabId, status, search, page, refresh, section, offset]);
  async function register() {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      const r = await api("/visits/visits", "POST", {
        sourceId,
        tabId,
        id: selected.id,
        version: selected.version,
        operationId: operation.current,
        confirmed: true,
        note,
        phone: visitPhone,
      });
      setSelected(null);
      setRefresh((n) => n + 1);
      notify(
        "Visita registrada no servidor. " + sheetLabel(r.sheetState) + ".",
      );
      returnFocus.current?.focus();
    } catch (e) {
      setError(textError(e));
    } finally {
      setBusy(false);
    }
  }
  async function create(newStreetConfirmed = false) {
    if (
      !window.confirm(
        `Confirmar cadastro de ${form.street}, ${form.number}, para ${form.resident}? Situação: ${form.status === "visited" ? "Visitado" : "Pendente"}.`,
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      await api("/visits/addresses", "POST", {
        ...form,
        sourceId,
        tabId,
        operationId: operation.current,
        confirmed: true,
        newStreetConfirmed,
        subgroupId: form.subgroupId || null,
      });
      setCreating(false);
      setRefresh((n) => n + 1);
      notify(
        "Endereço cadastrado no servidor. O envio à planilha será confirmado separadamente.",
      );
    } catch (e: any) {
      if (
        e.code === "VISIT_NEW_STREET" &&
        !newStreetConfirmed &&
        window.confirm(e.message)
      ) {
        setBusy(false);
        return create(true);
      }
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  const assignmentVersion =
    access?.assignments.find(
      (a) => a.sourceId === sourceId && a.tabId === tabId,
    )?.version ?? 0;
  return (
    <section className="visits-overview">
      <h1>Visitas</h1>
      <p>Endereços da planilha e aba atribuídas a você.</p>
      {error && <Banner kind="danger">{error}</Banner>}
      {!access ? (
        <p role="status">Conferindo acesso…</p>
      ) : !access.sources.length ? (
        <Banner>
          Nenhuma aba foi atribuída à sua conta. O administrador precisa validar
          os endereços e definir sua atribuição.
        </Banner>
      ) : (
        <>
          {access.manager && (
            <div className="vo-tabs">
              <Button onClick={() => setSection("work")}>Endereços</Button>
              <Button onClick={() => setSection("delegate")}>
                Delegar abas
              </Button>
              {access.admin && (
                <Button onClick={() => setSection("settings")}>
                  Configurações
                </Button>
              )}
            </div>
          )}
          {section !== "settings" && (
            <div className="vo-surface">
              <Field label="Planilha de origem">
                <select
                  value={sourceId}
                  onChange={(e) => setSource(e.target.value)}
                >
                  {access.sources.map((s) => (
                    <option value={s.id} key={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Aba do grupo">
                <select value={tabId} onChange={(e) => setTab(e.target.value)}>
                  {tabs.map((t) => (
                    <option value={t.id} key={t.id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}
          {section === "work" && (
            <>
              <div className="vo-tabs">
                {[
                  ["pending", "Pendentes"],
                  ["visited", "Visitados"],
                  ["analysis", "Em análise"],
                ].map(([v, label]) => (
                  <Button
                    key={v}
                    aria-pressed={status === v}
                    onClick={() => {
                      setStatus(v);
                      setPage(1);
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <Field label="Buscar rua, número ou morador">
                <input
                  value={search}
                  maxLength={240}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </Field>
              {list?.canCreate && (
                <Button
                  kind="primary"
                  onClick={() => {
                    const a = access.assignments.find(
                      (a) => a.sourceId === sourceId && a.tabId === tabId,
                    );
                    setForm({
                      street: "",
                      number: "",
                      neighborhood: "",
                      resident: "",
                      phone: "",
                      status: "pending",
                      groupId: a?.groupId ?? "",
                      subgroupId: a?.subgroupId ?? "",
                    });
                    operation.current = crypto.randomUUID();
                    setError("");
                    setCreating(true);
                  }}
                >
                  Adicionar endereço
                </Button>
              )}
              {!list && !error && <p role="status">Consultando endereços…</p>}
              {list && (
                <>
                  <p>{list.total.toLocaleString("pt-BR")} {list.total===1?'endereço':'endereços'}</p>
                  <ul className="vo-list">
                    {list.items.map((row) => (
                      <li className="vo-surface" key={row.id}>
                        <h2>
                          {row.street}, {row.number}
                        </h2>
                        <p>{row.neighborhood}</p>
                        {row.residents.length > 0 && (
                          <p>{row.residents.join(" · ")}</p>
                        )}
                        <p>
                          {row.status === "visited"
                            ? "Visitado"
                            : row.status === "analysis"
                              ? "Aguardando análise"
                              : "Pendente"}
                          {row.latest_visit_at
                            ? " · Última visita: " +
                              visitDate(row.latest_visit_at)
                            : ""}
                        </p>
                        <p className="small muted">
                          {sheetLabel(row.sheetState)}
                        </p>
                        {row.sheetError&&<p className="small muted">{row.sheetError}</p>}
                        <AddressMapLink address={row} />
                        {list.canVisit && row.status !== "analysis" && (
                          <Button
                            onClick={(e) => {
                              returnFocus.current = e.currentTarget;
                              operation.current = crypto.randomUUID();
                              setSelected(row);
                              setNote("");
                              setVisitPhone("");
                              setError("");
                            }}
                          >
                            {row.status === "visited"
                              ? "Registrar nova visita"
                              : "Registrar visita"}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                  {list.total === 0 && (
                    <p>Nenhum endereço nesta situação e busca.</p>
                  )}
                  {list.total > 20 && (
                    <div>
                      <Button
                        disabled={page === 1}
                        onClick={() => setPage((p) => p - 1)}
                      >
                        Anterior
                      </Button>{" "}
                      Página {page}{" "}
                      <Button
                        disabled={page * 20 >= list.total}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Próxima
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {section === "delegate" && access.manager && (
            <section className="vo-surface">
              <h2>Validar e delegar esta aba</h2>
              <p>
                Os grupos são conferidos somente nos contornos GRUPOS_GEOJSON.
                Somente endereços validados entram na delegação. Pendências ficam de fora e só entram após correção e nova delegação.
              </p>
              {!access.mapsConfigured && (
                <Banner>
                  A chave da Geocoding API ainda precisa ser configurada
                  privadamente no servidor.
                </Banner>
              )}
              {!review ? (
                <p role="status">Conferindo validações…</p>
              ) : (
                <>
                  <p>
                    {review.valid} de {review.total} endereços validados ·{" "}
                    {review.pending} ficam de fora desta delegação
                  </p>
                  {review.automation?.enabled && <p role="status">
                    Validação automática em segundo plano, com limite diário de consultas ao Google.
                    {review.automation.state==='paused' ? ` Pausada: ${review.automation.message} Retomada automática após ${new Date(review.automation.nextAt).toLocaleString('pt-BR')}.` : ' Os resultados aparecerão ao atualizar esta consulta.'}
                    {' '}Localizações imprecisas e grupos divergentes ficam pendentes para correção. Nenhuma delegação é feita automaticamente.
                  </p>}
                  <ul className="vo-list">
                    {review.items.map((r: any) => (
                      <li className="vo-record" key={r.id}>
                        <strong>
                          {r.street}, {r.number}
                        </strong>
                        <p>{r.neighborhood}</p>
                        {r.validationMessage && <p>{r.validationMessage}</p>}
                        <p>
                          {r.valid
                            ? "Localização e grupo conferidos"
                            : r.review?.zone
                              ? "Localizado em " +
                                r.review.zone +
                                " · conferir grupo na origem"
                              : "Localização pendente"}
                        </p>
                        {!r.valid && (
                          <Button
                            busy={busy}
                            onClick={async () => {
                              setBusy(true);
                              setError("");
                              try {
                                await api("/visits/review", "POST", {
                                  sourceId,
                                  tabId,
                                  id: r.id,
                                  force:true,
                                });
                                setRefresh((n) => n + 1);
                              } catch (e) {
                                setError(textError(e));
                              } finally {
                                setBusy(false);
                              }
                            }}
                          >
                            Conferir localização no Google
                          </Button>
                        )}
                        {!r.valid&&r.review?.state==='located'&&r.review?.groupId&&<Button busy={busy} onClick={async()=>{if(!window.confirm(`Solicitar correção do grupo deste endereço para ${r.review.zone} na planilha? Este endereço só entrará em uma nova delegação após a confirmação.`))return;setBusy(true);setError('');try{const result=await api('/visits/corrections','POST',{sourceId,tabId,id:r.id,confirmed:true});notify('Correção solicitada. '+sheetLabel(result.state)+'.');setRefresh(n=>n+1);}catch(e){setError(textError(e));}finally{setBusy(false);}}}>Corrigir grupo na planilha</Button>}
                      </li>
                    ))}
                  </ul>
                  <Button
                    disabled={offset === 0}
                    onClick={() => setOffset((n) => Math.max(0, n - 20))}
                  >
                    Anterior
                  </Button>{" "}
                  <Button
                    disabled={offset + 20 >= review.total}
                    onClick={() => setOffset((n) => n + 20)}
                  >
                    Próxima
                  </Button>
                </>
              )}
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setBusy(true);
                  setError("");
                  try {
                    await api("/visits/assignment", "PUT", {
                      ...assignment,
                      userId: assignment.userId || null,
                      subgroupId: assignment.subgroupId || null,
                      sourceId,
                      tabId,
                      version: assignmentVersion,
                    });
                    setRefresh((n) => n + 1);
                    notify("Atribuição da aba confirmada.");
                  } catch (e) {
                    setError(textError(e));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <Field label="Grupo desta aba">
                  <select
                    required
                    value={assignment.groupId}
                    onChange={(e) =>
                      setAssignment({
                        ...assignment,
                        groupId: e.target.value,
                        subgroupId: "",
                      })
                    }
                  >
                    <option value="">Escolha o grupo</option>
                    {access.groups
                      .filter((g) => !g.parentId)
                      .map((g) => (
                        <option value={g.id} key={g.id}>
                          {g.name}
                        </option>
                      ))}
                  </select>
                </Field>
                <Field label="Subgrupo (quando a aba for específica)">
                  <select
                    value={assignment.subgroupId}
                    onChange={(e) =>
                      setAssignment({
                        ...assignment,
                        subgroupId: e.target.value,
                      })
                    }
                  >
                    <option value="">Todos os subgrupos do grupo</option>
                    {access.groups
                      .filter((g) => g.parentId === assignment.groupId)
                      .map((g) => (
                        <option value={g.id} key={g.id}>
                          {g.name}
                        </option>
                      ))}
                  </select>
                </Field>
                <Field label="Responsável pela aba">
                  <select
                    value={assignment.userId}
                    onChange={(e) =>
                      setAssignment({ ...assignment, userId: e.target.value })
                    }
                  >
                    <option value="">Sem atribuição</option>
                    {access.users.map((u) => (
                      <option value={u.id} key={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </Field>
                {[
                  ["canCreate", "Pode adicionar endereço"],
                  ["canVisit", "Pode registrar visita"],
                  ["personal", "Pode consultar moradores"],
                ].map(([k, label]) => (
                  <label
                    key={k}
                    style={{ display: "block", padding: "12px 0" }}
                  >
                    <input
                      type="checkbox"
                      checked={assignment[k as "canCreate"]}
                      onChange={(e) =>
                        setAssignment({ ...assignment, [k]: e.target.checked })
                      }
                    />{" "}
                    {label}
                  </label>
                ))}
                <Button
                  type="submit"
                  kind="primary"
                  busy={busy}
                  disabled={
                    !!assignment.userId && (!review || review.valid === 0)
                  }
                >
                  Salvar atribuição
                </Button>
              </form>
            </section>
          )}
          {section === "settings" && access.admin && settings && (
            <form
              className="vo-surface"
              onSubmit={async (e) => {
                e.preventDefault();
                setBusy(true);
                setError("");
                try {
                  const saved = await api<VisitOperationConfig>(
                    "/visits/settings",
                    "PUT",
                    settings,
                  );
                  setSettings(saved);
                  notify("Configurações de Visitas publicadas.");
                } catch (e) {
                  setError(textError(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              <h2>Configurações de Visitas</h2>
              <p>
                Recompensa somente no primeiro registro elegível do endereço.
                Visitas já existentes na planilha e novas visitas ao mesmo
                endereço não concedem outra recompensa.
              </p>
              <Field label="Pontos da primeira visita">
                <input
                  inputMode="decimal"
                  value={settings.firstPoints ?? ""}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      firstPoints: e.target.value || null,
                    })
                  }
                />
              </Field>
              <Field label="XP da primeira visita">
                <input
                  inputMode="decimal"
                  value={settings.firstXP ?? ""}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      firstXP: e.target.value || null,
                    })
                  }
                />
              </Field>
              <p>
                Valores em branco aguardam configuração. Alterações não
                recalculam concessões anteriores.
              </p>
              <details>
                <summary>Gravação no Google Sheets</summary>
                <p>
                  Cada aba precisa de coluna de identificação estável,
                  mapeamento validado e data de visita configurada. A
                  confirmação do aplicativo e a confirmação da planilha são
                  estados separados.
                </p>
                <label style={{ display: "block", padding: 12 }}>
                  <input
                    type="checkbox"
                    checked={settings.sheetCoordinationConfirmed}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        sheetCoordinationConfirmed: e.target.checked,
                      })
                    }
                  />{" "}
                  Confirmo que a equipe não reorganizará linhas durante as
                  gravações do aplicativo.
                </label>
                <label style={{ display: "block", padding: 12 }}>
                  <input
                    type="checkbox"
                    checked={settings.writesEnabled}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        writesEnabled: e.target.checked,
                      })
                    }
                  />{" "}
                  Ativar envio das pendências às fontes configuradas
                </label>
              </details>
              <Button type="submit" kind="primary" busy={busy}>
                Publicar configurações
              </Button>
            </form>
          )}
        </>
      )}
      {selected && (
        <Modal
          title="Confirmar visita"
          mobileFullScreen
          onClose={() => setSelected(null)}
        >
          <h2>
            {selected.street}, {selected.number}
          </h2>
          <p>{selected.neighborhood}</p>
          {selected.status === "visited" && (
            <Banner>
              Este endereço já foi visitado. Uma nova visita não concede
              novamente os pontos da primeira.
            </Banner>
          )}
          {error && <Banner kind="danger">{error}</Banner>}
          <Field label="Telefone do munícipe (opcional)">
            <input type="tel" inputMode="tel" autoComplete="tel" maxLength={40}
              placeholder="DDD + telefone" value={visitPhone}
              onChange={e=>setVisitPhone(e.target.value)} />
          </Field>
          <p className="small muted">Se preenchido, atualiza o telefone deste cadastro. Em branco, mantém o telefone atual.</p>
          <Field label="Observação (opcional)">
            <textarea
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <Button onClick={() => setSelected(null)}>Voltar</Button>{" "}
          <Button kind="primary" busy={busy} onClick={() => void register()}>
            Confirmar visita
          </Button>
        </Modal>
      )}
      {creating && (
        <Modal
          title="Adicionar endereço"
          mobileFullScreen
          onClose={() => setCreating(false)}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void create();
            }}
          >
            {error && <Banner kind="danger">{error}</Banner>}
            {[
              ["street", "Rua (obrigatório)"],
              ["number", "Número (obrigatório)"],
              ["resident", "Nome do morador (obrigatório)"],
              ["phone", "Celular (opcional)"],
              [
                "neighborhood",
                "Bairro (opcional; será conferido automaticamente)",
              ],
            ].map(([k, label]) => (
              <Field key={k} label={label}>
                <input
                  required={["street", "number", "resident"].includes(k)}
                  value={form[k as "street"]}
                  maxLength={k === "number" ? 40 : 240}
                  inputMode={k === "phone" ? "tel" : undefined}
                  onChange={(e) => {
                    setForm({ ...form, [k]: e.target.value });
                    operation.current = crypto.randomUUID();
                  }}
                />
              </Field>
            ))}
            {access?.manager && (
              <Field label="Grupo da aba">
                <select
                  required
                  value={form.groupId}
                  onChange={(e) => {
                    setForm({
                      ...form,
                      groupId: e.target.value,
                      subgroupId: "",
                    });
                    operation.current = crypto.randomUUID();
                  }}
                >
                  <option value="">Escolha o grupo</option>
                  {access.groups
                    .filter((g) => !g.parentId)
                    .map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                </select>
              </Field>
            )}
            <Field label="Situação inicial">
              <select
                value={form.status}
                onChange={(e) => {
                  setForm({ ...form, status: e.target.value });
                  operation.current = crypto.randomUUID();
                }}
              >
                <option value="pending">Pendente</option>
                {list?.canVisit && <option value="visited">Visitado</option>}
              </select>
            </Field>
            <Button type="submit" kind="primary" busy={busy}>
              Conferir e cadastrar
            </Button>
          </form>
        </Modal>
      )}
    </section>
  );
}

